import { useLive, post, formatPhone, ago } from "../lib/api";
import type { Agendamento } from "../lib/api";
import { Card, Pill, Button, Empty, Carregando } from "../lib/ui";
import {
  Phone,
  Video,
  MapPin,
  Check,
  CheckCheck,
  X,
  CalendarDays,
  type LucideIcon,
} from "lucide-react";
import { AgendaDoCorretor } from "./AgendaGoogle";

/**
 * O que a IA marcou para o corretor.
 *
 * É o produto do atendimento no modo "marcar a conversa": em vez de uma fila
 * de gente para descobrir do zero, uma agenda com hora e contexto pronto.
 *
 * O contexto tem destaque de propósito. Um horário sem contexto obriga o
 * corretor a reler a conversa inteira antes de ligar — e quando ele não relê,
 * a ligação começa perguntando o que a pessoa já respondeu.
 */

const MODO: Record<
  Agendamento["modo"],
  { rotulo: string; icone: LucideIcon; peso: string }
> = {
  ligacao: { rotulo: "Ligação", icone: Phone, peso: "10 a 15 min" },
  video: { rotulo: "Vídeo", icone: Video, peso: "dá pra mostrar planta e mapa" },
  presencial: { rotulo: "Presencial", icone: MapPin, peso: "compromisso maior" },
};

export function Agenda() {
  const { data, reload } = useLive<Agendamento[]>("/agenda", [
    "agendamentos",
    "leads",
  ]);

  if (!data) return <Carregando />;

  if (data.length === 0) {
    return (
      <div>
        <AgendaDoCorretor />
        <Empty icone={CalendarDays}>
        Nada marcado ainda. Quando a IA fechar um horário, ele aparece aqui —
          com o resumo de quem você vai atender.
        </Empty>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <AgendaDoCorretor />
      <p className="text-xs text-ink-3 max-w-prose">
        {data.length} conversa{data.length > 1 ? "s" : ""} marcada
        {data.length > 1 ? "s" : ""}. O horário é como a pessoa falou — quem
        acerta o detalhe é você, na confirmação.
      </p>

      {data.map((a) => {
        const m = MODO[a.modo];
        return (
          <Card key={a.id} className="p-5" stripe={a.status === "confirmado" ? "ok" : "accent"}>
            <div className="flex flex-wrap items-start gap-4">
              <span className="w-10 h-10 rounded-xl bg-accent-dim text-accent grid place-items-center shrink-0">
                <m.icone size={18} />
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-ink font-semibold">
                    {a.lead_name ?? "Sem nome"}
                  </span>
                  <span className="text-xs tabular-nums text-ink-3">
                    {formatPhone(a.lead_phone)}
                  </span>
                  <Pill tone="accent">{m.rotulo}</Pill>
                  {a.status === "confirmado" && <Pill tone="ok">confirmado</Pill>}
                </div>

                <p className="text-base font-semibold text-ink mt-1.5">
                  {a.quando_texto}{" "}
                  <span className="text-xs font-normal text-ink-3">· {m.peso}</span>
                </p>

                {/* O contexto é o que faz a ligação começar adiantada. */}
                {a.contexto && (
                  <p className="text-sm text-ink-2 mt-3 leading-relaxed px-3.5 py-2.5 rounded-lg bg-surface-2 border border-rule">
                    {a.contexto}
                  </p>
                )}

                <span className="text-[11px] text-ink-3 block mt-2">
                  marcado há {ago(a.created_at)}
                </span>
              </div>

              <div className="flex gap-1.5 shrink-0">
                {a.status !== "confirmado" && (
                  <Button
                    icone={Check}
                    onClick={async () => {
                      await post(`/agenda/${a.id}/status`, {
                        status: "confirmado",
                      });
                      reload();
                    }}
                  >
                    Confirmar
                  </Button>
                )}
                <Button
                  variant="primary"
                  icone={CheckCheck}
                  onClick={async () => {
                    await post(`/agenda/${a.id}/status`, {
                      status: "realizado",
                    });
                    reload();
                  }}
                >
                  Aconteceu
                </Button>
                <Button
                  variant="ghost"
                  icone={X}
                  title="Cancelar este agendamento"
                  className="hover:!text-danger"
                  onClick={async () => {
                    if (!confirm(`Cancelar a ${m.rotulo.toLowerCase()} com ${a.lead_name ?? "este lead"}?`))
                      return;
                    await post(`/agenda/${a.id}/status`, {
                      status: "cancelado",
                    });
                    reload();
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
